import { useReactFlow } from '@xyflow/react';
import { orderBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import {
  FlowChartProps,
  Node,
  useFlowChartContext,
} from '@/components/diagrams/FlowChart';
import { useHelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';
import { useSectionNodesBounds } from '@/components/diagrams/FlowChart/nodes/SectionLabelNode/hooks';
import { isTileNode } from '@/components/diagrams/FlowChart/utils/nodes';
import { isTextNoteNode } from '@/components/diagrams/FlowChart/utils/textNoteUtils';

import { useEstateWaterfallContext } from '../contexts/estateWaterfall.context';

export function useFlowChartProps(initiallyOpenEditModalNodeId?: string) {
  const { getNodes } = useReactFlow();
  const { isInteractive } = useIsInteractive();
  const { dropTargetNode } = useFlowChartContext();
  const { state, dispatch, getState } = useEstateWaterfallContext();
  const { helperLineVertical, helperLineHorizontal, applyHelperLinesToNodes } =
    useHelperLines();
  const { applySectionBoundsToNodes } = useSectionNodesBounds();

  const onNodeClick = useCallback<NonNullable<FlowChartProps['onNodeClick']>>(
    (_event, node) => {
      if (node.type === 'sectionLabel') return;

      // In edit mode, only the default re-position interaction is allowed.
      if (isInteractive) return;

      // Clicking on the active summary node should close the pane
      if (node.id === state.summaryNodeId) {
        dispatch({ type: 'CLOSE_SUMMARY_PANEL' });
        return;
      }

      dispatch({ type: 'OPEN_SUMMARY_PANEL', nodeId: node.id });
    },
    [dispatch, isInteractive, state.summaryNodeId]
  );

  const onNodeDragStart = useCallback<
    NonNullable<FlowChartProps['onNodeDragStart']>
  >(
    (_event, node) => {
      dispatch({ type: 'NODE_DRAG_START', nodeId: node.id });
    },
    [dispatch]
  );

  const onNodeDragStop = useCallback<
    NonNullable<FlowChartProps['onNodeDragStop']>
  >(
    (_event, node) => {
      const draggingNode = node;
      const willGroup = Boolean(dropTargetNode && draggingNode);

      dispatch({
        type: 'NODE_DRAG_STOP',
        nodeId: node.id,
        skipHistoryEntry: willGroup,
      });

      if (!willGroup) return;

      setTimeout(() => {
        // Deconflict with other actions that are dispatched when nodes change
        dispatch({
          type: 'UPSERT_NODE_GROUP',
          groupNodeId: dropTargetNode!.id,
          appendNodesIds: [draggingNode.id],
        });
      });
    },
    [dispatch, dropTargetNode]
  );

  const onNodeMouseEnter = useCallback<
    NonNullable<FlowChartProps['onNodeMouseEnter']>
  >(
    (event, node) => {
      // No relevant code paths when non tile node (section group), or in edit mode
      if (node.type !== 'tile' || isInteractive) return;
      dispatch({ type: 'NODE_MOUSE_ENTER', event, node });
    },
    [dispatch, isInteractive]
  );

  const onNodeMouseLeave = useCallback<
    NonNullable<FlowChartProps['onNodeMouseLeave']>
  >(
    (event, node) => {
      // No relevant code paths when non tile node (section group), or in edit mode
      if (node.type !== 'tile' || isInteractive) return;
      dispatch({ type: 'NODE_MOUSE_LEAVE', event, node });
    },
    [dispatch, isInteractive]
  );

  const onNodesChange = useCallback<
    NonNullable<FlowChartProps['onNodesChange']>
  >(
    (changes) => {
      const nodes = applyHelperLinesToNodes(
        changes,
        applySectionBoundsToNodes(changes, getNodes() as Node[])
      );

      dispatch({ type: 'NODES_CHANGED', nodes, changes });
    },
    [applyHelperLinesToNodes, applySectionBoundsToNodes, dispatch, getNodes]
  );

  const onEdgeMouseEnter = useCallback<
    NonNullable<FlowChartProps['onEdgeMouseEnter']>
  >(
    (event, edge) => {
      // No relevant code paths in edit mode
      if (isInteractive) return;

      /**
       * Note: for some reason, edge mouse enter events can happen in rapid succession.
       * This doesn't occur for nodes, maybe because the edge hover area is so small?
       * Either way, we guard against it here as a stop gap from many events firing.
       */
      if (getState().highlightedIds.has(edge.id)) return;

      dispatch({ type: 'EDGE_MOUSE_ENTER', event, edge });
    },
    [dispatch, getState, isInteractive]
  );

  const onEdgeMouseLeave = useCallback<
    NonNullable<FlowChartProps['onEdgeMouseLeave']>
  >(
    (event, edge) => {
      // No relevant code paths in edit mode
      if (isInteractive) return;
      dispatch({ type: 'EDGE_MOUSE_LEAVE', event, edge });
    },
    [dispatch, isInteractive]
  );

  const onEdgeClick = useCallback<NonNullable<FlowChartProps['onEdgeClick']>>(
    (_event, edge) => {
      // No relevant code paths in edit mode
      if (isInteractive) return;

      // When edge is clicked, dispatch the EDGE_LABEL_CLICKED action
      // This will trigger the same behavior as clicking the edge label button
      const edgeId = edge.id;
      dispatch({ type: 'EDGE_LABEL_CLICKED', edgeId });
    },
    [dispatch, isInteractive]
  );

  const onPaneClick = useCallback<
    NonNullable<FlowChartProps['onPaneClick']>
  >(() => {
    if (getState().derivedData.isSummaryPanelOpen) {
      dispatch({ type: 'CLOSE_SUMMARY_PANEL' });
    }
  }, [dispatch, getState]);

  // Draw the highlighted edges on top of the others
  const orderedEdges = useMemo(
    () => orderBy(state.edges, (edge) => edge.data?.highlight),
    [state.edges]
  );

  const onNodeResizeStart = useCallback(
    (nodeId: string) => {
      dispatch({ type: 'NODE_RESIZE_START', nodeId });
    },
    [dispatch]
  );

  const onNodeResizeEnd = useCallback(
    (nodeId: string) => {
      dispatch({ type: 'NODE_RESIZE_END', nodeId });
    },
    [dispatch]
  );

  const onDeleteTextNote = useCallback(
    (nodeId: string) => {
      dispatch({ type: 'DELETE_TEXT_NOTE', nodeId });
    },
    [dispatch]
  );

  const onTextNoteNotesChange = useCallback(
    (nodeId: string, notes: string) => {
      dispatch({ type: 'UPDATE_TEXT_NOTE', nodeId, notes });
    },
    [dispatch]
  );

  const nodesWithCallbacks = useMemo(
    () =>
      state.nodes.map((node): Node => {
        if (isTextNoteNode(node)) {
          return {
            ...node,
            // Text notes have no summary/click behavior, so they shouldn't be
            // selectable (or show the selectable pointer cursor) outside edit
            // mode — clicking is a no-op then.
            selectable: isInteractive,
            data: {
              ...node.data,
              onNodeResizeStart,
              onNodeResizeEnd,
              onDelete: onDeleteTextNote,
              onNotesChange: onTextNoteNotesChange,
            },
          };
        }

        if (!isTileNode(node)) return node;

        return {
          ...node,
          data: {
            ...node.data,
            initiallyOpenEditModal: node.id === initiallyOpenEditModalNodeId,
            onNodeResizeStart,
            onNodeResizeEnd,
          },
        };
      }),
    [
      state.nodes,
      isInteractive,
      initiallyOpenEditModalNodeId,
      onNodeResizeStart,
      onNodeResizeEnd,
      onDeleteTextNote,
      onTextNoteNotesChange,
    ]
  );

  const flowChartProps: FlowChartProps = {
    style: state.isInitializing ? { opacity: 0 } : undefined,
    nodes: nodesWithCallbacks,
    edges: orderedEdges,
    hideEdges: state.waterfall.visualizationConfig?.hideEdges,
    // Let a two-finger swipe on a trackpad pan the diagram, and let a pinch on
    // a trackpad or touchscreen zoom it. A mouse wheel also pans, because the
    // browser cannot tell a trackpad swipe from a mouse wheel.
    panOnScroll: true,
    zoomOnPinch: true,
    onNodeDragStart,
    onNodeDragStop,
    onNodeClick,
    onNodesChange,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onEdgeClick,
    onPaneClick,
    getState,
  };

  return { flowChartProps, helperLineHorizontal, helperLineVertical };
}
